import {FC, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {FormikHelpers, FormikProps} from 'formik';
import {Popup, PopupFooter} from '@tickchak/react-share/app';
import useShow, {TShow} from '../../../../hooks/useShow';
import FormContext from '../../../components/formContext/FormContext';
import SectionBox from '../manageLecturePopup/SectionBox/SectionBox';
import Input from '../../../../component/input/Input';
import TextEditorFormik from '../../../../component/textEditor/TextEditorFormik';
import TimeSlotField from '../manageLecturePopup/fields/TimeSlotField';
import TimeSlotSelectPopup from '../manageLecturePopup/fields/TimeSlotSelectPopup';
import CoffeeCup from '../../../../assets/icons/CoffeeCup';
import {getSlots} from '../../../../redux/timetable/timetableActions';
import {RootState} from '../../../../redux/store.type';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {useBreakForm} from './useBreakForm';
import SummaryHeader from './SummaryHeader';
import {DESCRIPTION_MAX} from './SummaryHeader';
import * as css from './ManageBreakPopup.css';

export interface BreakFormValues {
    title: string;
    tsid: number | null;
    description: string;
}

interface ManageBreakPopupProps {
    show: TShow;
    breakItem?: TTimetableItem;
}

const ManageBreakPopup: FC<ManageBreakPopupProps> = ({show, breakItem}) => (
    <Popup show={show} data-cy="manageBreakPop">
        <BreakPopupContent key={`${breakItem?.ttid ?? 'new'}-${show.isOpen}`} show={show} breakItem={breakItem} />
    </Popup>
);

const BreakPopupContent: FC<{show: TShow; breakItem?: TTimetableItem}> = ({show, breakItem}) => {
    const {t} = useTranslation('timetable');
    const slotShow = useShow();
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const isEditing = !!breakItem;
    const {initialValues, validationSchema, handleSubmit, dayTimestamp} = useBreakForm(show, breakItem);

    useEffect(() => {
        if(!slots.length) getSlots();
    }, []);

    const onSubmit = async (
        changed: unknown,
        values: BreakFormValues,
        actions: FormikHelpers<BreakFormValues>,
    ) => {
        const success = await handleSubmit(changed, values, actions);
        if(success) show.close();
    };

    return (
        <FormContext
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({submitForm, isSubmitting}: FormikProps<BreakFormValues>) => {
                const handleClose = show.close;
                return (
                    <Popup.Tabs
                        tabs={[]}
                        value=""
                        onChange={() => {}}
                        hasInnerPopup={slotShow.isOpen}
                        header={
                            <Popup.Header
                                title={isEditing ? t('editBreak') : t('manageBreak')}
                                onClose={handleClose}
                            />
                        }
                    >
                        <css.form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                            <SectionBox title={t('sectionBreakDetails')}>
                                <css.detailsRow>
                                    <Input
                                        name="title"
                                        text={t('breakName')}
                                        icon={<CoffeeCup />}
                                        events={{}}
                                        className=""
                                        forwardRef={null}
                                    />
                                    <TimeSlotField show={slotShow} name="tsid" label={t('timeSlot')} />
                                </css.detailsRow>
                            </SectionBox>

                            <SectionBox title={t('sectionMoreInfo')}>
                                <css.summaryField>
                                    <SummaryHeader />
                                    <TextEditorFormik
                                        name="description"
                                        showToolbar
                                        limit={{maxChar: DESCRIPTION_MAX, showCharCounter: false}}
                                        minHeight="140px"
                                        placeholder={t('summaryPlaceholder')}
                                        disableVariables
                                    />
                                </css.summaryField>
                            </SectionBox>
                        </css.form>
                        <css.footerWrap>
                            <PopupFooter
                                startActions={[
                                    {label: t('cancel'), variant: 'neutral', onClick: handleClose, disabled: isSubmitting},
                                    {label: isEditing ? t('saveBreak') : t('createBreak'), onClick: submitForm, disabled: isSubmitting},
                                ]}
                            />
                        </css.footerWrap>
                        <TimeSlotSelectPopup show={slotShow} name="tsid" dayTimestamp={dayTimestamp} />
                    </Popup.Tabs>
                );
            }}
        </FormContext>
    );
};

export default ManageBreakPopup;
